import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { DeleteIcon } from '@/components/molecules/settings/icon/delete-icon'
import { AssetType } from '@/models/guest-app/asset'
import { ContainerHeader } from '@/components/molecules/settings/container-header'
import { ContainerSidePanelBody, Props as SidePanelProps } from '@/components/molecules/settings/container-side-panel-body'
import { AssetDetailContext } from '@/components/pages/setting/guest-app/asset-detail'
import { deleteMobileRequestCategories, updateAsset, updateAssetRequestCategoryDisplayOrder } from '@/apis/aipass'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { useDraggableDom } from '@/components/molecules/settings/use-draggable-dom'
import { CategoryType } from '@/models/mobile-request'
import { RequestCategoryDetailModal } from './request-category-detail-modal'
import { GuestAppTableHeader, GuestAppTableBody, GuestAppTableRow } from '@/components/atoms/settings/guest-app-table'

type Props = {
  asset: AssetType & { categories: CategoryType[] }
}

export const RequestCategoryList: React.FC<Props & SidePanelProps> = ({ asset, menus }) => {
  const { t } = useTranslation()
  const [formValue, setFormValue] = useState<{ title: string; categories: CategoryType[] }>({
    title: asset.title,
    categories: asset.categories,
  })
  const [deleteIds, setDeleteIds] = useState<number[]>([])
  const [showDetailProp, setShowDetailProp] = useState<CategoryType | undefined | null>(undefined)
  const { doReload, setIsLoading, setIsEditing, listenDoSave, listenDoCancel } = useContext(AssetDetailContext)
  const { errorHandler } = useErrorHandler()
  const addDeleteTarget = (id: number) => {
    setFormValue({ ...asset, categories: formValue.categories.filter(detail => detail.id !== id) })
    setDeleteIds([...deleteIds, id])
    setIsEditing(true)
  }
  const changeDisplayOrder = (newList: CategoryType[]) => {
    setFormValue({ ...asset, categories: newList })
    setIsEditing(true)
  }
  const { dragStart, dragOver, dragDrop, handleTouchStart, handleTouchMove, handleTouchEnd } = useDraggableDom({
    list: formValue.categories,
    onChange: changeDisplayOrder,
  })

  const saveFormValue = async () => {
    try {
      setIsLoading(true)
      if (deleteIds.length) {
        await deleteMobileRequestCategories(deleteIds)
        setDeleteIds([])
      }
      if (formValue.categories.length) {
        await updateAssetRequestCategoryDisplayOrder(
          asset.id,
          formValue.categories.map(detail => detail.id),
        )
      }
      await updateAsset(asset.id, { title: formValue.title })
      doReload()
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (listenDoSave) saveFormValue()
  }, [listenDoSave])

  useEffect(() => {
    if (listenDoCancel) {
      setDeleteIds([])
      doReload()
    }
  }, [listenDoCancel])

  useEffect(() => {
    if (!asset) {
      return
    }
    setFormValue({
      title: asset.title,
      categories: asset.categories,
    })
  }, [asset])

  return (
    <>
      <ContainerHeader
        goBack
        title={formValue.title}
        onChangeTitle={(newTitle: string) => {
          setFormValue({ ...formValue, title: newTitle })
          setIsEditing(true)
        }}
        onClickAddButton={() => setShowDetailProp(null)}
      />
      <ContainerSidePanelBody menus={menus}>
        <>
          <GuestAppTableHeader>
            <div css={thumbnailColStyle}></div>
            <div css={titleColStyle}>{t('Title')}</div>
            <div css={deleteColStyle}></div>
          </GuestAppTableHeader>
          <GuestAppTableBody>
            {formValue.categories.map(detail => (
              <GuestAppTableRow
                id={detail.id.toString()}
                key={detail.id}
                onClick={() => setShowDetailProp(detail)}
                draggable="true"
                onDragStart={dragStart}
                onDragOver={dragOver}
                onDrop={dragDrop}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <div css={thumbnailColStyle}>
                  <img src={detail.imagePath}></img>
                </div>
                <div css={titleColStyle}>{detail.name.ja}</div>
                <div css={deleteColStyle}>
                  <DeleteIcon onClick={() => addDeleteTarget(detail.id)} />
                </div>
              </GuestAppTableRow>
            ))}
          </GuestAppTableBody>
        </>
      </ContainerSidePanelBody>
      {showDetailProp !== undefined && (
        <RequestCategoryDetailModal
          asset={asset}
          detail={showDetailProp}
          onCancel={() => setShowDetailProp(undefined)}
          onSave={() => setShowDetailProp(undefined)}
        />
      )}
    </>
  )
}

const thumbnailColStyle = css({
  marginLeft: 62,
  marginRight: 24,
  width: 'calc(38px * 16 / 9)',
  '> img': {
    height: 38,
    borderRadius: 5,
  },
})
const titleColStyle = css({
  flexGrow: 1,
  overflowWrap: 'anywhere',
  width: 'min-content',
})
const deleteColStyle = css({
  width: 30,
  margin: '0 16px',
})
